import { pickString, plural, type ToolState } from './_base'

/**
 * Parse a streamed `ScanProgressChunkDTO` from a tool part's raw output. The
 * AI SDK yields each chunk as an object (or a JSON string); we fall back to the
 * already-parsed terminal result for legacy tool-invocation parts.
 */
export function readScanChunk(
  rawOutput: unknown,
  fallback: unknown
): Record<string, unknown> | null {
  if (rawOutput && typeof rawOutput === 'object') {
    return rawOutput as Record<string, unknown>
  }
  if (typeof rawOutput === 'string') {
    try {
      const parsed = JSON.parse(rawOutput)
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, unknown>
      }
    } catch {
      /* ignore */
    }
  }
  return fallback && typeof fallback === 'object'
    ? (fallback as Record<string, unknown>)
    : null
}

/**
 * Derive the scan status line from the latest streamed chunk. Pure so the
 * streaming / complete / failed branches can be unit-tested without a DOM.
 *
 * `checks.scan` streams `ScanProgressChunkDTO` chunks over the AI SDK's
 * preliminary-tool-results channel: while running, each yielded chunk lands in
 * `part.output` with `preliminary: true`; only the FINAL chunk
 * (`complete`/`failed`) has `preliminary: false`.
 */
export function deriveScanStatus(input: {
  chunk: Record<string, unknown> | null
  isPreliminary: boolean
  baseState: ToolState
}): {
  state: ToolState
  loadingDetail: string | null
  completedDetail: string | null
} {
  const { chunk, isPreliminary, baseState } = input

  // A `failed` terminal chunk carries no ok/success/error field, so
  // isToolError can't see it — surface it as an error explicitly.
  const failedReason =
    !isPreliminary && chunk?.type === 'failed'
      ? pickString(chunk?.reason)
      : null

  const state: ToolState =
    isPreliminary && !baseState.isError
      ? { ...baseState, isComplete: false }
      : failedReason !== null
        ? { ...baseState, isError: true, rawError: failedReason }
        : baseState

  // Live progress while scanning: prefer the streamed counts, fall back to the
  // requested subset size before the first chunk lands.
  const planned =
    typeof chunk?.plannedTotal === 'number'
      ? chunk.plannedTotal
      : Array.isArray(baseState.args?.checkIds)
        ? baseState.args.checkIds.length
        : null
  const scanned =
    typeof chunk?.scannedCount === 'number' ? chunk.scannedCount : null
  const currentCheck = pickString(chunk?.checkId)
  const loadingDetail =
    planned !== null
      ? `${scanned ?? 0}/${planned} checks${currentCheck ? ` · ${currentCheck}` : ''}`
      : pickString(baseState.args?.provider)

  // Completed summary comes from the final `complete` chunk's
  // ScanFindingsSummaryDTO (failedChecks / totalChecks).
  const summaryRaw = chunk?.summary
  const summary =
    summaryRaw && typeof summaryRaw === 'object'
      ? (summaryRaw as Record<string, unknown>)
      : null
  const failedChecks =
    typeof summary?.failedChecks === 'number' ? summary.failedChecks : null
  const totalChecks =
    typeof summary?.totalChecks === 'number' ? summary.totalChecks : null
  const completedDetail =
    failedChecks !== null
      ? `${plural(failedChecks, 'finding')}${totalChecks !== null ? ` of ${plural(totalChecks, 'check')}` : ''}`
      : totalChecks !== null
        ? `${plural(totalChecks, 'check')}`
        : pickString(baseState.args?.provider)

  return { state, loadingDetail, completedDetail }
}
